import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import theme from '../../styles/theme';
import { useResponsive } from '../../hooks/useResponsive';
import BrandLogo from './BrandLogo';
import { canShowLink } from '../../utils/accessControl';
import { ROLES } from '../../constants/roles';

/**
 * Navigation bar component with sober, clean design.
 */
const Navbar = ({ onLoginClick, onMenuClick }) => {
  const { t } = useTranslation();
  const { isAuthenticated, user, logout } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  // The sidebar is a drawer on both mobile and tablet, so the hamburger must
  // be available on both.
  const showMenuButton = isMobile || isTablet;
  const navigate = useNavigate();
  const [adminOpen, setAdminOpen] = useState(false);
  const adminRef = useRef(null);
  const canSeeAdminSection = canShowLink(user?.roles || [], { minimumRole: ROLES.ADMIN });
  const canSeeSuperAdminSection = canShowLink(user?.roles || [], { minimumRole: ROLES.SUPER_ADMIN });

  const handleLogout = async () => {
    await logout();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (adminRef.current && !adminRef.current.contains(e.target)) {
        setAdminOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isMobile && adminOpen) {
      setAdminOpen(false);
    }
  }, [isMobile, adminOpen]);

  const styles = {
    navbar: {
      backgroundColor: theme.colors.primary.main,
      padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.md} ${theme.spacing.xl}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      boxShadow: theme.shadows.md,
      color: theme.colors.primary.contrast,
      minHeight: isMobile ? '56px' : '64px',
      gap: theme.spacing.sm,
    },
    brandSection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.sm,
      minWidth: 0,
    },
    menuButton: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '44px',
      height: '44px',
      border: isMobile ? 'none' : `1px solid rgba(255,255,255,0.35)`,
      borderRadius: theme.borderRadius.md,
      backgroundColor: 'transparent',
      color: theme.colors.primary.contrast,
      cursor: 'pointer',
      flexShrink: 0,
    },
    brand: {
      margin: 0,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: isMobile ? theme.spacing.sm : theme.spacing.md,
      minWidth: 0,
    },
    userInfo: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.primary.contrast,
      opacity: 0.9,
      display: isMobile ? 'none' : 'block',
    },
    userName: {
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.primary.contrast
    },
    button: {
      backgroundColor: theme.colors.accent.main,
      color: theme.colors.accent.contrast,
      border: 'none',
      padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.lg}`,
      borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.sm,
      cursor: 'pointer',
      transition: theme.transitions.fast,
      fontWeight: theme.typography.fontWeight.semibold,
      minHeight: '44px',
    },
    logoutButton: {
      backgroundColor: 'transparent',
      color: theme.colors.primary.contrast,
      border: isMobile ? 'none' : `1px solid rgba(255,255,255,0.5)`,
      padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.lg}`,
      borderRadius: isMobile ? 0 : theme.borderRadius.full,
      fontSize: theme.typography.fontSize.sm,
      cursor: 'pointer',
      transition: theme.transitions.fast,
      fontWeight: theme.typography.fontWeight.medium,
      minHeight: '44px',
      textDecoration: isMobile ? 'underline' : 'none',
    },
    adminButton: {
      backgroundColor: 'transparent',
      color: theme.colors.primary.contrast,
      border: `1px solid rgba(255,255,255,0.5)`,
      padding: isMobile ? `${theme.spacing.sm} ${theme.spacing.md}` : `${theme.spacing.sm} ${theme.spacing.lg}`,
      borderRadius: theme.borderRadius.full,
      fontSize: theme.typography.fontSize.sm,
      cursor: 'pointer',
      transition: theme.transitions.fast,
      fontWeight: theme.typography.fontWeight.medium,
      minHeight: '44px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing.xs,
    },
    adminWrapper: {
      position: 'relative',
    },
    adminDropdown: {
      position: 'absolute',
      top: 'calc(100% + 8px)',
      right: 0,
      backgroundColor: theme.colors.primary.dark,
      border: `2px solid rgba(255,255,255,0.45)`,
      borderRadius: '4px',
      boxShadow: '0 16px 36px rgba(8, 20, 32, 0.45)',
      minWidth: '220px',
      zIndex: 1000,
      overflow: 'hidden',
    },
    adminDropdownItem: {
      display: 'block',
      width: '100%',
      padding: `${theme.spacing.md} ${theme.spacing.md}`,
      backgroundColor: 'transparent',
      border: 'none',
      borderBottom: '1px solid rgba(255,255,255,0.18)',
      textAlign: 'left',
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.primary.contrast,
      cursor: 'pointer',
      fontWeight: theme.typography.fontWeight.semibold,
      letterSpacing: '0.02em',
    },
  };

  return (
    <nav style={styles.navbar}>
      <div style={styles.brandSection}>
        {isAuthenticated && showMenuButton && (
          <button
            style={styles.menuButton}
            onClick={onMenuClick}
            aria-label={t('sidebar.toggleMenu', 'Toggle navigation menu')}
          >
            <Menu size={20} />
          </button>
        )}
        <h1 style={styles.brand}>
          <BrandLogo
            label={t('navbar.brand')}
            size={isMobile ? 30 : 34}
            textSize={isMobile ? theme.typography.fontSize.lg : theme.typography.fontSize['2xl']}
            markColor="rgba(255,255,255,0.18)"
            symbolColor="#FFFFFF"
            accentColor={theme.colors.accent.main}
            textColor={theme.colors.primary.contrast}
          />
        </h1>
      </div>
      
      <div style={styles.rightSection}>
        {isAuthenticated ? (
          <>
            <div style={styles.userInfo}>
              {t('navbar.hello')}, <span style={styles.userName}>{user?.name || user?.email}</span>
            </div>
            {canSeeAdminSection && !isMobile && (
              <div style={styles.adminWrapper} ref={adminRef}>
                <button
                  style={styles.adminButton}
                  onClick={() => setAdminOpen((o) => !o)}
                  onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
                  onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  {t('navbar.admin', 'Admin')}
                  <ChevronDown size={14} />
                </button>
                {adminOpen && (
                  <div style={styles.adminDropdown}>
                    <button
                      style={styles.adminDropdownItem}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onClick={() => { setAdminOpen(false); navigate('/app/admin'); }}
                    >
                      {t('navbar.adminUserManagement', 'User Management')}
                    </button>
                    <button
                      style={styles.adminDropdownItem}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onClick={() => { setAdminOpen(false); navigate('/app/contacts'); }}
                    >
                      {t('navbar.adminContacts', 'Contact Messages')}
                    </button>
                    <button
                      style={styles.adminDropdownItem}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      onClick={() => { setAdminOpen(false); navigate('/app/widget'); }}
                    >
                      {t('widget.admin.title', 'Widget Submissions')}
                    </button>
                    {canSeeSuperAdminSection && (
                      <button
                        style={{ ...styles.adminDropdownItem, borderBottom: 'none' }}
                        onMouseOver={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                        onMouseOut={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                        onClick={() => { setAdminOpen(false); navigate('/app/platform'); }}
                      >
                        {t('navbar.adminPlatformTenants', 'Platform Tenants')}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
            <button 
              style={styles.logoutButton}
              onClick={handleLogout}
              onMouseOver={(e) => { e.target.style.backgroundColor = 'rgba(255,255,255,0.15)'; }}
              onMouseOut={(e) => { e.target.style.backgroundColor = 'transparent'; }}
            >
              {t('navbar.logout')}
            </button>
          </>
        ) : (
          <button 
            style={styles.button}
            onClick={onLoginClick}
            onMouseOver={(e) => { e.target.style.backgroundColor = theme.colors.accent.dark; }}
            onMouseOut={(e) => { e.target.style.backgroundColor = theme.colors.accent.main; }}
          >
            {t('navbar.login')}
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
